import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Expander from 'components/utils/Expander'
import useToggle from 'hooks/useToggle'
import SectionHeading from 'tripPlanner/components/Timeline/SectionHeading'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { itinerarySectionElementId, SUGGESTED } from 'tripPlanner/utils/itinerary'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { useDeleteTripItems } from 'tripPlanner/hooks/api/tripItem'
import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import NonDraggableItemWrapper from 'tripPlanner/components/DragAndDrop/NonDraggableItemWrapper'
import TimelineTripItem from 'tripPlanner/components/Timeline/TimelineTripItem'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Clickable from 'components/Common/Clickable'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'

import { SectionContainer } from '../common'
import ConfirmDismissSuggestions from './ConfirmDismissSuggestions'
import Group from 'components/utils/Group'

const FlexClickable = styled(Clickable)`
  display: flex;
`

interface Props {
  items: Array<TripItem>
  headerOffset: number
}

function SuggestedSection({ items, headerOffset }: Props) {
  const intl = useIntl()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const { value: menuOpen, toggle: toggleMenu, off: closeMenu } = useToggle()
  const { value: isCollapsed, toggle: toggleCollapsed } = useToggle()

  const { mutate: deleteItems } = useDeleteTripItems({
    onSuccess: () => {
      showSnackbar(intl.formatMessage({
        id: '5PpInTnyE8IGvKThvcWE',
        defaultMessage: 'Recommendations dismissed',
        description: 'Success snackbar message after dismissing trip recommendations',
      }), 'success')
    },
    onError: () => {
      showSnackbar(intl.formatMessage({
        id: 'aKD7HhnQdnJjbYtsdn99',
        defaultMessage: 'Failed to dismiss recommendations',
        description: 'Error snackbar message when dismissing trip recommendations fails',
      }), 'critical')
    },
  })

  const showModal = useContext(ModalContext)

  const onClickDelete = useCallback(async() => {
    const confirmed = await showModal<boolean>(<ConfirmDismissSuggestions />)
    if (confirmed) {
      deleteItems({ itemIds: items.map((item) => item.id), tripId: items[0].tripId })
    }
  }, [deleteItems, items, showModal])

  return <div id={itinerarySectionElementId(SUGGESTED)}>
    <SectionHeading
      title={intl.formatMessage({
        id: 'X3xrCawaOJ2awcXTndZy',
        defaultMessage: 'Recommended for you',
        description: 'Section heading for AI-recommended trip items in the itinerary',
      })}
      section={SUGGESTED}
      top={headerOffset}
      endContent={<Group direction="horizontal" gap={16}>
        <FlexClickable onClick={toggleMenu} ref={menuButtonRef}>
          <LineEllipsisVIcon colour="neutral-one" />
        </FlexClickable>
        <FlexClickable onClick={toggleCollapsed}>
          <DropdownChevron open={!isCollapsed} colour="neutral-one" />
        </FlexClickable>
      </Group>}
    />

    <DropdownList
      anchorRef={menuButtonRef}
      triggerRef={menuButtonRef}
      open={menuOpen}
      onClose={closeMenu}
      size="S"
      placement="bottom-end"
    >
      <ClickableListItem
        title={intl.formatMessage({
          id: 'HpLRIN2GLWxaUHCrtH2b',
          defaultMessage: 'Dismiss recommendations',
          description: 'Menu item to dismiss all AI-generated trip recommendations',
        })}
        onClick={onClickDelete}
      />
    </DropdownList>

    <Expander expanded={!isCollapsed}>
      <SectionContainer direction="vertical" gap={16} desktopGap={28}>
        {items.map((item) => (
          <NonDraggableItemWrapper key={item.id}>
            <TimelineTripItem item={item} presentationType="start" variant="drawer" />
          </NonDraggableItemWrapper>
        ))}
      </SectionContainer>
    </Expander>
  </div>
}

export default SuggestedSection
